import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _dec3, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _LocalePreferenceMachine;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 - 2019 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { BaseMachine } from "abreact/incubator/ui/machine/BaseMachine";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { I18NService } from "abweb/service/i18n/I18NService";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { TranslatorManager } from "abweb/service/locale/TranslatorManager";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
export let LocalePreferenceMachine = (_dec = observable.shallow, _dec2 = observable.shallow, _dec3 = action.bound, _class = (_LocalePreferenceMachine = class LocalePreferenceMachine extends BaseMachine {
  constructor() {
    super();
    _initializerDefineProperty(this, "localeIndex", _descriptor, this);
    _initializerDefineProperty(this, "_supportedLocales", _descriptor2, this);
    _initializerDefineProperty(this, "_localeChoices", _descriptor3, this);
    _initializerDefineProperty(this, "_localeTranslators", _descriptor4, this);
    _initializerDefineProperty(this, "_state", _descriptor5, this);
    _initializerDefineProperty(this, "_i18nPrompt", _descriptor6, this);
    _initializerDefineProperty(this, "_i18nInfo", _descriptor7, this);
    _initializerDefineProperty(this, "_originalLocale", _descriptor8, this);
    _defineProperty(this, "localeService", AbWebServices.localeService);
    makeObservable(this);
    this.localeIndex = undefined;
    this._supportedLocales = undefined;
    this._localeChoices = undefined;
    this._localeTranslators = undefined;
    this._i18nPrompt = undefined;
    this._i18nInfo = undefined;
    this._originalLocale = undefined;
    this._state = "initialized";
    this.registerDestroyListener(reaction(() => this.localeIndex, this.refreshI18NStrings));
  }
  get newLocale() {
    return this.localeIndex === LocalePreferenceMachine.DEFAULT_LOCALE ? null : this._lastLocale;
  }
  get state() {
    return this._state;
  }
  get i18nInfo() {
    return this._i18nInfo;
  }
  get localeChoices() {
    // cypress 'rpc tests' have been known to logout so fast that _localeChoices is not set.
    return this._localeChoices?.slice() ?? [];
  }
  get i18nPrompt() {
    return this._i18nPrompt;
  }
  async prepare() {
    this.setState("fetching_supported_locales");
    const temp = await this.localeService.getSupportedLocales();
    const supportedLocales = temp.filter(value => value !== null);
    const localeTranslators = new Map();
    for (let i = 0; i < supportedLocales.length; i++) {
      const locale = supportedLocales[i];
      const localeTM = new TranslatorManager(locale);
      await localeTM.loadResourceBundles(["webapp/abweb/assets/locale/{locale}/abweb.json"], []);
      localeTranslators.set(locale, new I18NService(localeTM.translator));
    }
    runInAction(() => {
      this._supportedLocales = supportedLocales;
      this._localeChoices = supportedLocales.map(locale => {
        return {
          locale
        };
      });
      this._localeTranslators = localeTranslators;

      // * null preferred locale means that the user has picked "Default", so we want to use the current locale
      // * non-null preferred locale means that the user has picked a specific locale, so we want to use that
      if (LocaleManager.preferredLanguageLocale === null) {
        this._originalLocale = LocaleManager.languageLocale;
        this.localeIndex = -1;
      } else {
        this._originalLocale = LocaleManager.preferredLanguageLocale;
        this.localeIndex = this._supportedLocales.indexOf(this._originalLocale);
      }
    });
    this.setState("ready");
  }
  localeAtIndex(index) {
    return this._supportedLocales[index];
  }
  refreshI18NStrings(requestedIndex) {
    const selectedLocale = requestedIndex >= 0 ? this.localeAtIndex(requestedIndex) : LocaleManager.languageLocale;
    if (selectedLocale == null || selectedLocale === this._lastLocale) {
      return;
    }
    const i18n = this._localeTranslators.get(selectedLocale);
    runInAction(() => {
      this._lastLocale = selectedLocale;
      this._i18nPrompt = AbWebResources.localePreferenceDlg_prompt();
      this._i18nInfo = AbWebResources.localePreferenceDlg_info();
    });

    // replace entire array
    const localeChoices = this._supportedLocales.map(locale => {
      const languageKey = "locale.language." + locale;
      const translatedLanguage = i18n.getMessage("abweb", languageKey);
      if (locale === selectedLocale) {
        return {
          locale,
          translatedLanguage
        }; // translated IS the native language for the locale
      } else {
        const nativeI18N = this._localeTranslators.get(locale);
        const nativeLanguage = nativeI18N.getMessage("abweb", languageKey);
        return {
          locale,
          translatedLanguage,
          nativeLanguage
        };
      }
    });
    runInAction(() => {
      this._localeChoices = localeChoices;
      this.localeIndex = requestedIndex;
    });
  }
  setCancelled() {
    this.setState("cancelled");
  }
  setComplete() {
    this.setState("ok");
  }
  async commit() {
    await this.changeLocale(this._originalLocale, this.newLocale);
    return true;
  }
  setState(value) {
    runInAction(() => {
      this._state = value;
    });
  }
  async changeLocale(origLocale, newLocale) {
    if (origLocale === newLocale) {
      return;
    }
    LocalePreferenceMachine.LOG3.info(`Changing language preference from locale ${origLocale} to ${newLocale}`);

    // do NOT bother doing expensive locale change (async load of res bundles and kendo culture files)
    // as browser is just going to reload anyway
    // LocaleManager.locale = newLocale;
    const negotiatedLocaleTag = await AbWebServices.localeService.setPreferredLocale(newLocale);
    if (negotiatedLocaleTag !== newLocale) {
      // Defensive: The rpc for supported locales (which drives the dialog) and the
      // server localization/supported/ai-locales_*.properties are out of sync.
      // Log so that it shows up in PFS
      LocalePreferenceMachine.LOG3.error(`Unable to have server set preferred locale to ${newLocale}.\n` + "  Possible cause is that the server does not have the corresponding" + ` localization/supported/ai-locales_${newLocale}.properties file defined.`);
    }
    BrowserUtils.locationReload(); // do not directly invoke document.location.reload so that can mock it
  }
}, _defineProperty(_LocalePreferenceMachine, "LOG3", LogUtils.getLogger(__filename)), _defineProperty(_LocalePreferenceMachine, "DEFAULT_LOCALE", -1), _LocalePreferenceMachine), _descriptor = _applyDecoratedDescriptor(_class.prototype, "localeIndex", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_supportedLocales", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_localeChoices", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_localeTranslators", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "_state", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "_i18nPrompt", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "_i18nInfo", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "_originalLocale", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "newLocale", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "newLocale"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "i18nPrompt", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "i18nPrompt"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refreshI18NStrings", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "refreshI18NStrings"), _class.prototype), _class);
//# sourceMappingURL=LocalePreferenceMachine.js.map
